import React from 'react';

import * as Sentry from '@sentry/browser';
import moment from 'moment';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import TextField from '@material-ui/core/TextField';

import { loadJobRoles } from '../../action/load-job-roles';
import { loadOrgJobs } from '../../job/actions/load-org-jobs';
import { loadOrganizationScrapedImages } from '../../organization-scraped-image/actions/load-organization-scraped-images';
import { updateOrganizationScrapedImage } from '../../organization-scraped-image/actions/update-organization-scraped-image';
import {
  organizationScrapedImageForOrgIdSelector,
  organizationScrapedImagesStateForOrgIdSelector,
} from '../../organization-scraped-image/organization-scraped-image-selectors';
import { jobRoleSelector } from '../../reducers/job-role-selectors';
import { jobsForOrgIdSelector } from '../../reducers/job-selectors';
import { orgSelector } from '../../reducers/org-selectors';

import * as Api from '../../api/api';
import * as FileUploadApi from '../../api/file-upload-api-client';
import { UiButton } from '../../ui/ui-button';

import { JobFlowButtons } from './job-flow-buttons';

import { IMAGE_PATH } from '../../config/config';
import asyncComponent from '../../util/async-component';
import { CustomerImageEditor } from '../../widgets/customer-image-editor';
import { ImageGallery } from '../../widgets/image-gallery';
import { LoadingMessage } from '../../widgets/loading-message';

const ReactPlayer = asyncComponent(() => import('react-player'));

const mapStateToProps = (state: any, { orgId, job }: InputProps) => ({
  org: orgSelector(state, orgId),
  jobs: jobsForOrgIdSelector(state, orgId),
  jobRole: job.jobRoleId ? jobRoleSelector(state, job.jobRoleId) : null,
  organizationScrapedImages: organizationScrapedImageForOrgIdSelector(state, orgId),
  organizationScrapedImagesState: organizationScrapedImagesStateForOrgIdSelector(state, orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    loadOrgJobs: () => dispatch(loadOrgJobs(orgId)),
    loadJobRoles: () => dispatch(loadJobRoles()),
    loadOrganizationScrapedImages: () => dispatch(loadOrganizationScrapedImages(orgId)),
    updateOrganizationScrapedImage: (
      organizationScrapedImageId: number,
      update: OrganizationScrapedImageUpdateRequest,
    ) => dispatch(updateOrganizationScrapedImage(organizationScrapedImageId, update)),
  },
});

interface InputProps extends JobFlowStep {
  orgId: number;
  job: Partial<Job>;
}

type State = {
  imagePath: string;
  fullResImagePath: string;
  uploaderVisible: boolean;
  isUploading: boolean;
  videoFormVisible: boolean;
  videoUrl: string | null | undefined;
  videoError: boolean;
  isChoosingFromExistingImages: boolean;
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & StateProps & DispatchProps;

const MIN_IMAGE_SIZE = 120; // px
class JobFlowImageComponent extends React.PureComponent<Props, State> {
  state = {
    imagePath: this.props.job.imagePath || '',
    fullResImagePath: this.props.job.fullResImagePath || '',
    uploaderVisible: false,
    isUploading: false,
    videoUrl: this.props.job.videoUrl,
    videoFormVisible: Boolean(this.props.job.videoUrl),
    videoError: false,
    isChoosingFromExistingImages: false,
  };

  componentDidMount() {
    this.props.actions.loadJobRoles();
    this.props.actions.loadOrganizationScrapedImages();
  }

  componentDidUpdate() {
    this.props.actions.loadJobRoles();
    this.props.actions.loadOrganizationScrapedImages();
  }

  handleVideoLinkClick = () => {
    this.setState({ videoFormVisible: true });
  };

  handleVideoUrlChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    this.props.onDirty(true);
    this.setState({
      videoUrl: e.target.value,
      videoError: false,
    });
  };

  handleVideoError = () => {
    this.setState({ videoError: true });
  };

  toggleExistingJobImageSelector = () => {
    this.setState((prevState) => ({
      isChoosingFromExistingImages: !prevState.isChoosingFromExistingImages,
    }));
  };

  handleExistingJobImageClick = (image: ImageGalleryImage) => {
    const job = image.value;
    if (!job.imagePath || !job.fullResImagePath) {
      Sentry.captureMessage('User tried to select existing job image but image paths were null');
      this.setState({ isChoosingFromExistingImages: false });
      return;
    }

    this.setState({
      isChoosingFromExistingImages: false,
      imagePath: job.imagePath,
      fullResImagePath: job.fullResImagePath,
    });
  };

  handleOrganizationScrapedImageClick = (image: ImageGalleryImage) => {
    this.setState({
      isChoosingFromExistingImages: false,
      imagePath: image.value,
      fullResImagePath: image.value,
    });
  };

  handleDeleteOrganizationScrapedImageClick = (image: ImageGalleryImage) => {
    this.props.actions.updateOrganizationScrapedImage(image.id, {
      deletedAt: moment.utc().format(),
    });
  };

  handleUseDefaultImageClick = () => {
    const { jobRole } = this.props;
    if (jobRole) {
      this.setState({
        imagePath: jobRole.adImagePath,
        fullResImagePath: jobRole.adImageFullResPath,
      });
    }
  };

  handleNext = () => {
    const { jobRole } = this.props;
    let { imagePath, fullResImagePath } = this.state;
    const { videoUrl } = this.state;

    if (jobRole && !imagePath && !fullResImagePath) {
      imagePath = jobRole.adImagePath;
      fullResImagePath = jobRole.adImageFullResPath;
    }

    this.props.onNext({ imagePath, fullResImagePath, videoUrl });
  };

  handleUploadClick = () => {
    this.setState({ uploaderVisible: true });
  };

  handleImageUpload = async ({
    file,
    fullResFile,
    fileExtension,
  }: {
    file: Blob | null;
    fullResFile: any;
    fileExtension: string | null;
  }) => {
    const { org, job, onDirty } = this.props;
    if (!org || !file) {
      return;
    }
    const fileName = `${org.org_key}_${job.job_id}_sourcing_${new Date().getTime()}`;
    const imagePath = `customer/${org.org_key}/${fileName}.${fileExtension}`;
    const fullResImagePath = `customer/${org.org_key}/${fileName}_fullRes.${fileExtension}`;

    this.setState({ isUploading: true }, async () => {
      if (file && fullResFile && fileExtension) {
        await Api.get(`/org/${org.org_id}/upload-policy`)
          .then((r) => r.json())
          .then((params) => {
            FileUploadApi.uploadFile(
              params.fileUploadPolicy,
              params.orgPolicy.bucket,
              imagePath,
              file,
            );
            FileUploadApi.uploadFile(
              params.fileUploadPolicy,
              params.orgPolicy.bucket,
              fullResImagePath,
              fullResFile,
            );
          });

        onDirty(true);
        this.setState({ imagePath, fullResImagePath });
      }
      this.setState({ isUploading: false });
    });
  };

  render() {
    const {
      onPrev,
      onSkip,
      job,
      jobRole,
      saving,
      editing,
      jobs,
      organizationScrapedImages,
      organizationScrapedImagesState,
    } = this.props;
    const {
      uploaderVisible,
      isUploading,
      videoFormVisible,
      videoUrl,
      videoError,
      isChoosingFromExistingImages,
      imagePath,
    } = this.state;

    if (!jobs || !organizationScrapedImagesState || organizationScrapedImagesState.loading) {
      return <LoadingMessage />;
    }

    const jobImagePath = imagePath ? `${IMAGE_PATH}/${imagePath}` : null;

    const jobsWithCustomImages = jobs.reduce<Array<ImageGalleryImage>>((acc, j) => {
      if (
        // IF (01/25/21): Include the job in the selector if...
        j.imagePath && // it has an image
        !j.imagePath.includes('job-role-default-image') && // it's not using one of our defaults
        j.imagePath !== imagePath && // it's image is not already being used by the job being created/edited
        !acc.find((jobWithCustomImage) => jobWithCustomImage.imageUrl === j.imagePath) // another job with the same image hasn't been added already
      ) {
        acc.push({
          id: j.job_id,
          value: j,
          imageUrl: `${IMAGE_PATH}/${j.imagePath}`,
          alt: j.title,
        });
      }
      return acc;
    }, []);

    const orgImageGalleryImages = organizationScrapedImages.map((osi) => ({
      id: osi.organizationScrapedImageId,
      value: osi.imagePath,
      imageUrl: `${IMAGE_PATH}/${osi.imagePath}`,
      alt: 'An image scraped from a customer website',
    }));

    return (
      <>
        <div className="card u-padding-lg u-textAlign-center u-margin-top-xl">
          <div className="typo-h6">
            {!uploaderVisible &&
              !isChoosingFromExistingImages &&
              `Here's ${
                editing && imagePath ? 'your job image' : 'an image that could work for your job'
              }`}
            {uploaderVisible && 'Upload a new image'}
            {isChoosingFromExistingImages && 'Select one of your existing images'}
          </div>
          <div className="typo-subtitle u-maxWidth-550px u-display-inlineBlock">
            The image should relate to the job and be catchy and interesting. The best images often
            include people or specific relevant equipment.
          </div>
          {uploaderVisible && (
            <div className="u-display-inlineBlock u-maxWidth-450px u-margin-top-xl">
              <CustomerImageEditor onUpload={this.handleImageUpload} fixedWidth={400} />
            </div>
          )}
          {isChoosingFromExistingImages && (
            <>
              {jobsWithCustomImages.length > 0 ? (
                <>
                  <div className="u-margin-top typo-bold">Images from other jobs</div>
                  <ImageGallery
                    images={jobsWithCustomImages}
                    imageDisplayLimit={5}
                    onClick={this.handleExistingJobImageClick}
                  />
                </>
              ) : null}
              {orgImageGalleryImages.length > 0 ? (
                <>
                  <div className="typo-bold">Images from your website</div>
                  <ImageGallery
                    images={orgImageGalleryImages}
                    imageDisplayLimit={5}
                    onClick={this.handleOrganizationScrapedImageClick}
                    onDelete={this.handleDeleteOrganizationScrapedImageClick}
                    minimumSize={MIN_IMAGE_SIZE}
                  >
                    <UiButton
                      size="sm"
                      className="u-margin-top"
                      variant="secondary"
                      onClick={this.handleUseDefaultImageClick}
                    >
                      Use Default Image
                    </UiButton>
                  </ImageGallery>
                </>
              ) : null}
              <div className="u-textAlign-left">
                <UiButton variant="dismiss" onClick={this.toggleExistingJobImageSelector}>
                  Cancel
                </UiButton>
              </div>
            </>
          )}
          {!uploaderVisible && !isChoosingFromExistingImages && jobRole && (
            <>
              {orgImageGalleryImages.length > 0 && !jobImagePath ? (
                <ImageGallery
                  images={orgImageGalleryImages}
                  imageDisplayLimit={8}
                  onClick={this.handleOrganizationScrapedImageClick}
                  onDelete={this.handleDeleteOrganizationScrapedImageClick}
                  minimumSize={MIN_IMAGE_SIZE}
                >
                  <UiButton
                    size="sm"
                    className="u-margin-top"
                    variant="secondary"
                    onClick={this.handleUseDefaultImageClick}
                  >
                    Use Default Image
                  </UiButton>
                </ImageGallery>
              ) : (
                <img
                  className="u-maxWidth-450px u-width-100 u-margin-top-lg u-margin-bottom-lg u-borderRadius-3"
                  src={jobImagePath || `${IMAGE_PATH}/${jobRole.adImagePath}`}
                  alt={job.title}
                />
              )}
              <div className="u-width-100 u-display-flex u-justifyContent-spaceBetween">
                <div>
                  {!videoFormVisible && (
                    <UiButton size="sm" onClick={this.handleVideoLinkClick} variant="dismiss">
                      Have a video link?
                    </UiButton>
                  )}
                </div>
                <div>
                  {(jobsWithCustomImages.length > 0 || orgImageGalleryImages.length > 0) && (
                    <UiButton
                      size="sm"
                      className="u-margin-right"
                      variant="tertiary"
                      onClick={this.toggleExistingJobImageSelector}
                    >
                      Reuse An Existing Image
                    </UiButton>
                  )}
                  <UiButton size="sm" onClick={this.handleUploadClick} variant="tertiary">
                    {editing && job.imagePath ? 'Upload A New Image' : 'Upload Your Own Image'}
                  </UiButton>
                </div>
              </div>
            </>
          )}
        </div>
        {videoFormVisible && (
          <div className="card u-textAlign-left u-padding-lg u-textAlign-center u-margin-top-xl">
            <div className="typo-h6">Video</div>
            <div className="typo-subtitle">
              Have a video hosted somewhere like YouTube? Copy in your video link below and
              we&apos;ll display it to your job applicants instead of the image. (e.g.
              https://www.youtube.com/...)
            </div>
            <TextField
              className="u-margin-top-lg u-margin-bottom-lg"
              value={videoUrl}
              variant="outlined"
              onChange={this.handleVideoUrlChange}
              label="Video URL"
              margin="dense"
              fullWidth
            />
            {videoUrl && !videoError && (
              <ReactPlayer
                className="u-margin-0Auto"
                width="80%"
                url={videoUrl}
                controls
                onError={this.handleVideoError}
              />
            )}
            {videoError && (
              <div className="u-color-red">Sorry, there was a problem loading this video.</div>
            )}
          </div>
        )}
        <JobFlowButtons
          onNext={this.handleNext}
          onPrev={onPrev}
          onSkip={onSkip}
          editing={editing}
          saving={saving || isUploading}
        />
      </>
    );
  }
}

export const JobFlowImage = connect(mapStateToProps, mapDispatchToProps)(JobFlowImageComponent);
